import type { CalEvent, WorkingHours } from "../types";
import { addDays, dayStart, fd, minutesOfDay } from "./time";

/*
 * Day-view calculations, ported from the approved mockup's renderDay /
 * layout / conflicts logic and applied to real events.
 */

/** The timeline shows the whole day, 00:00–24:00. */
export const DAY_END_MIN = 24 * 60;
/** Working day used until the user's Outlook work hours load (the mockup's 09:00–17:30). */
export const WORK_START_MIN = 9 * 60;
export const WORK_END_MIN = 17 * 60 + 30;
export const DEFAULT_WORKING_HOURS: WorkingHours = {
  days: new Set([1, 2, 3, 4, 5]),
  startMin: WORK_START_MIN,
  endMin: WORK_END_MIN,
  source: "default",
};
export const CELL_MIN = 30;
/** 48px per hour, as in the mockup. */
export const PX_PER_MIN = 0.8;
export const GAP_ROW_MIN = 45;

/** An event's footprint on one day, in wall-clock minutes (0–1440). */
export interface DaySegment {
  readonly event: CalEvent;
  readonly startMin: number;
  readonly endMin: number;
}

export interface Clash {
  /** Starts first. */
  readonly a: CalEvent;
  readonly b: CalEvent;
  readonly startMin: number;
  readonly endMin: number;
  /** Sorted ids — identifies the pair. */
  readonly key: string;
  /** Changes when either meeting moves, so a "Leave It" stops applying. */
  readonly signature: string;
}

export type EventVisual = "declined" | "focus" | "conflict" | "draft" | "invite" | "tentative" | "default";

export interface AvailabilityCell {
  readonly startMin: number;
  readonly state: "free" | "busy" | "conf";
  readonly past: boolean;
}

/** Counts as booked time and can clash: not cancelled, declined or shown as free. */
export function isActive(e: CalEvent): boolean {
  return !e.isCancelled && e.response !== "no" && e.showAs !== "free";
}

/**
 * Your own meeting whose invite (or latest change) hasn't been sent yet: Outlook
 * saves one when a meeting with attendees is closed without Send. Graph marks
 * it `isDraft`; it is sent from Outlook.
 */
export function isUnsentDraft(e: CalEvent): boolean {
  return e.isDraft && e.isOrganizer && !e.isCancelled;
}

export function needsReply(e: CalEvent): boolean {
  return !e.isOrganizer && e.responseRequested && e.response === "wait" && !e.isCancelled;
}

/** A focus block: your own appointment, nobody invited, titled "Focus time". */
export function isFocus(e: CalEvent): boolean {
  return e.isOrganizer && e.attendeeCount === 0 && e.subject.toLowerCase() === "focus time";
}

export function displaySubject(e: CalEvent): string {
  return e.subject || "(No title)";
}

/** Timed events on `day`, cut at midnight, sorted by start then longest first. */
export function timedSegments(events: readonly CalEvent[], day: string): DaySegment[] {
  const from = dayStart(day).getTime();
  const to = dayStart(addDays(day, 1)).getTime();
  const out: DaySegment[] = [];
  for (const event of events) {
    if (event.isAllDay) continue;
    const s = event.start.getTime();
    const e = event.end.getTime();
    if (e <= from || s >= to) continue;
    out.push({
      event,
      startMin: s <= from ? 0 : minutesOfDay(event.start),
      endMin: e >= to ? DAY_END_MIN : minutesOfDay(event.end),
    });
  }
  return out.sort(
    (x, y) => x.startMin - y.startMin || y.endMin - y.startMin - (x.endMin - x.startMin) || x.event.id.localeCompare(y.event.id),
  );
}

export function allDayEvents(events: readonly CalEvent[], day: string): CalEvent[] {
  const t = dayStart(day).getTime();
  return events.filter((e) => e.isAllDay && e.start.getTime() <= t && e.end.getTime() > t);
}

/** Side-by-side columns for overlapping events (the mockup's layout()). */
export function layoutColumns(segs: readonly DaySegment[]): Map<string, { col: number; cols: number }> {
  const res = new Map<string, { col: number; cols: number }>();
  let cluster: string[] = [];
  let columns: number[] = [];
  let clusterEnd = -1;
  const flush = () => {
    for (const id of cluster) {
      const entry = res.get(id);
      if (entry) res.set(id, { col: entry.col, cols: columns.length });
    }
    cluster = [];
    columns = [];
    clusterEnd = -1;
  };
  for (const s of segs) {
    if (cluster.length && s.startMin >= clusterEnd) flush();
    let col = columns.findIndex((end) => end <= s.startMin);
    if (col < 0) {
      col = columns.length;
      columns.push(s.endMin);
    } else {
      columns[col] = s.endMin;
    }
    res.set(s.event.id, { col, cols: 1 });
    cluster.push(s.event.id);
    clusterEnd = Math.max(clusterEnd, s.endMin);
  }
  flush();
  return res;
}

/**
 * Plain code-unit order, the order sort() uses by default. A clash's key is
 * stored on the device when it is left as is, so the key must not change.
 */
const byCodeUnit = (a: string, b: string) => (a < b ? -1 : Number(a > b));

function clashSignature(a: CalEvent, b: CalEvent): string {
  return [a, b]
    .sort((x, y) => x.id.localeCompare(y.id))
    .map((e) => `${e.start.getTime()}-${e.end.getTime()}`)
    .join("|");
}

/**
 * Overlapping active meetings. Meetings that only touch (one ends as the next
 * starts) are not a clash. Pairs the user chose to leave are skipped until
 * either meeting's time changes.
 */
export function findClashes(segs: readonly DaySegment[], acks: Readonly<Record<string, string>>): Clash[] {
  const out: Clash[] = [];
  const open: DaySegment[] = [];
  for (const s of segs) {
    if (!isActive(s.event)) continue;
    for (let i = open.length - 1; i >= 0; i--) {
      const o = open[i];
      if (o && o.endMin <= s.startMin) open.splice(i, 1);
    }
    for (const o of open) {
      const key = [o.event.id, s.event.id].sort(byCodeUnit).join("|");
      const signature = clashSignature(o.event, s.event);
      if (acks[key] === signature) continue;
      out.push({
        a: o.event,
        b: s.event,
        startMin: s.startMin,
        endMin: Math.min(o.endMin, s.endMin),
        key,
        signature,
      });
    }
    open.push(s);
  }
  return out;
}

export function clashIds(clashes: readonly Clash[]): Set<string> {
  const ids = new Set<string>();
  for (const c of clashes) {
    ids.add(c.a.id);
    ids.add(c.b.id);
  }
  return ids;
}

/** The mockup's evClass precedence: declined → focus → clash → needs reply → tentative. */
export function eventVisual(e: CalEvent, clashing: ReadonlySet<string>): EventVisual {
  if (e.isCancelled || e.response === "no") return "declined";
  if (isFocus(e)) return "focus";
  if (clashing.has(e.id)) return "conflict";
  if (isUnsentDraft(e)) return "draft";
  if (needsReply(e)) return "invite";
  if (e.response === "may") return "tentative";
  return "default";
}

/** Where `day` sits relative to today, for greying out the past. */
export function pastCutoffMin(day: string, today: string, nowMin: number): number {
  if (day < today) return DAY_END_MIN;
  if (day > today) return 0;
  return nowMin;
}

/** 30-minute cells across the working day: clash, busy, free, and whether they are past. */
export function availabilityCells(
  segs: readonly DaySegment[],
  pastMin: number,
  window: { startMin: number; endMin: number },
): AvailabilityCell[] {
  const active = segs.filter((s) => isActive(s.event));
  const cells: AvailabilityCell[] = [];
  const from = Math.floor(window.startMin / CELL_MIN) * CELL_MIN;
  const to = Math.ceil(window.endMin / CELL_MIN) * CELL_MIN;
  for (let m = from; m < to; m += CELL_MIN) {
    const n = active.filter((s) => s.startMin < m + CELL_MIN && s.endMin > m).length;
    let state: AvailabilityCell["state"] = "free";
    if (n > 1) state = "conf";
    else if (n === 1) state = "busy";
    cells.push({ startMin: m, state, past: m + CELL_MIN <= pastMin });
  }
  return cells;
}

/** Working hours on `day`, or null when it isn't a working day. */
export function workWindow(hours: WorkingHours, day: string): { startMin: number; endMin: number } | null {
  return hours.days.has(dayStart(day).getDay()) ? { startMin: hours.startMin, endMin: hours.endMin } : null;
}

/**
 * Hour labels under the availability strip, as a position along it (0–1):
 * every 2 hours (every hour for a short day), plus both ends.
 */
export function axisTicks(window: { startMin: number; endMin: number }): { min: number; at: number }[] {
  const span = window.endMin - window.startMin;
  if (span <= 0) return [];
  const step = span <= 6 * 60 ? 60 : 120;
  const ticks = [window.startMin];
  for (let t = Math.ceil((window.startMin + 1) / step) * step; t < window.endMin; t += step) {
    if (t - window.startMin >= 45 && window.endMin - t >= 45) ticks.push(t);
  }
  ticks.push(window.endMin);
  return ticks.map((min) => ({ min, at: (min - window.startMin) / span }));
}

/** Your free minutes inside the working window, in 15-minute steps. */
export function freeMinutes(segs: readonly DaySegment[], window: { startMin: number; endMin: number }): number {
  const active = segs.filter((s) => isActive(s.event));
  let free = 0;
  for (let m = window.startMin; m < window.endMin; m += 15) {
    if (!active.some((s) => s.startMin < m + 15 && s.endMin > m)) free += 15;
  }
  return free;
}

/** What is happening now, or next, today. Focus blocks are skipped. */
export function nextUp(segs: readonly DaySegment[], nowMin: number): { seg: DaySegment; current: boolean } | null {
  const candidates = segs.filter((s) => isActive(s.event) && !isFocus(s.event));
  const current = candidates.find((s) => s.startMin <= nowMin && s.endMin > nowMin);
  if (current) return { seg: current, current: true };
  const next = candidates.find((s) => s.startMin > nowMin);
  return next ? { seg: next, current: false } : null;
}

/** Header subtitle: "3 meetings · 2h 15min booked", or "Nothing booked". */
export function bookedSummary(segs: readonly DaySegment[]): string {
  const booked = segs.filter((s) => isActive(s.event) && !isFocus(s.event));
  if (booked.length === 0) return "Nothing booked";
  const minutes = booked.reduce((total, s) => total + (s.endMin - s.startMin), 0);
  return `${booked.length} meeting${booked.length === 1 ? "" : "s"} · ${fd(minutes)} booked`;
}
